/* MQS V198 — Model-specific frame + required roller + priced optional accessories.
 * Separate, SKU-checked Shopify variants. No price or inventory overrides.
 * Component products are selected in Theme settings > Concentricity gauge configuration.
 * Never intercept ordinary product/service forms. No automatic cart retries.
 */
(() => {
  'use strict';
  if (customElements.get('mqs-gauge-builder')) return;
  const ROLLER_FAMILIES = {"10": ["110-10FP", "110-10HP", "110-10QP", "110-10FZ", "110-10HZ", "110-10QZ", "110-10TZ"], "20": ["120-20FP", "120-20HP", "120-20QP"], "40": ["140-40FP", "140-40HP", "140-40QP"], "HL40": ["140-40HS"]};
  const upper = value => String(value || '').trim().toUpperCase();
  const moneyValue = value => Number.isSafeInteger(Number(value)) && Number(value) > 0;
  const localURL = value => {
    const url = new URL(value, location.origin);
    if (url.origin !== location.origin) throw new Error('An item has an invalid product link.');
    return url;
  };
  function groupId() {
    return 'gauge-' + (window.crypto && crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2));
  }
  async function requestJSON(url, options = {}, timeout = 18000) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(url, { credentials:'same-origin', ...options, signal:controller.signal });
      const text = await response.text();
      let data;
      try { data = JSON.parse(text); } catch (_) { throw new Error('The store returned an unreadable response.'); }
      if (!response.ok) {
        const detail = typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : 'The store could not complete this request.';
        throw new Error(detail);
      }
      return data;
    } catch (error) {
      if (error.name === 'AbortError') throw new Error('The store took too long to respond.');
      throw error;
    } finally { clearTimeout(timer); }
  }
  class MQSGaugeBuilder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.form = this.closest('form');
      this.button = this.querySelector('[data-gauge-add]');
      this.status = this.querySelector('[data-gauge-status]');
      if (!this.form || !this.button || !this.status) {
        // A registered custom element can connect before its children are parsed.
        if ((this.connectAttempts || 0) < 20) {
          this.connectAttempts = (this.connectAttempts || 0) + 1;
          requestAnimationFrame(() => { if (this.isConnected) this.connectedCallback(); });
        }
        return;
      }
      this.connectAttempts = 0;
      this.ready = true;
      this.busy = false;
      this.uncertain = false;
      this.submissionComplete = false;
      this.rows = Array.from(this.querySelectorAll('[data-gauge-option]'));
      this.validRollers = new Set(ROLLER_FAMILIES[this.dataset.rollerFamily] || []);
      this.rollersIncluded = this.dataset.rollersIncluded === 'true';
      this.quantityInput = this.form.querySelector('[name="quantity"]');
      this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style:'currency', currency:this.dataset.currency || 'GBP'
      });
      this.events = new AbortController();
      const signal = this.events.signal;
      window.addEventListener('pageshow', event => {
        if (!event.persisted || !this.busy) return;
        if (this.submissionComplete) {
          this.lock(false);
          this.setStatus('Your previous selection was added. Review your basket before adding another configuration.');
        } else {
          this.uncertain = true;
          this.querySelector('[data-gauge-review-cart]').hidden = false;
          this.lock(false);
          this.setStatus('Review your basket before trying again, to avoid duplicate items.', true);
        }
      }, {signal});
      this.querySelector('[data-gauge-clear-roller]')?.addEventListener('click', () => {
        if (this.busy || this.uncertain) return;
        this.rows.filter(row => row.dataset.role === 'roller').forEach(row => row.querySelector('[data-gauge-choice]').checked = false);
        this.setStatus(''); this.refresh();
      }, {signal});
      this.button.addEventListener('click', event => { event.preventDefault(); this.addSelection(); }, {signal});
      this.addEventListener('change', () => {
        if (!this.uncertain && !this.busy) this.setStatus('');
        this.refresh();
      }, {signal});
      this.form.addEventListener('input', () => this.refresh(), {signal});
      this.form.addEventListener('change', () => this.refresh(), {signal});
      this.form.addEventListener('focusout', event => {
        // Warehouse may normalise a typed quantity in its focusout listener.
        if (event.target === this.quantityInput) setTimeout(() => this.refresh(), 0);
      }, {signal});
      // Warehouse updates the quantity on its delegated click handler.
      this.form.addEventListener('click', event => {
        if (event.target.closest('[data-action="increase-picker-quantity"], [data-action="decrease-picker-quantity"]')) setTimeout(() => this.refresh(), 0);
      }, {signal});
      this.refresh();
    }
    disconnectedCallback() {
      if (this.events) this.events.abort();
      this.ready = false;
    }
    money(cents) { return this.formatter.format(Number(cents) / 100); }
    setStatus(message, error = false) {
      this.status.textContent = message;
      this.status.classList.toggle('is-error', error);
    }
    quantity() {
      const n = Number(this.quantityInput ? this.quantityInput.value : 1);
      return Number.isSafeInteger(n) && n >= 1 && n <= 9999 ? n : 0;
    }
    componentQuantity(row) {
      const field = row.querySelector('[data-gauge-component-quantity]');
      const n = field ? Number(field.value) : 1;
      return Number.isSafeInteger(n) && n >= 1 && n <= 99 ? n : 0;
    }
    validFrame(frame) {
      return Boolean(this.dataset.frameModel && ROLLER_FAMILIES[this.dataset.rollerFamily] && frame.sku && frame.productId && frame.variantId);
    }
    selectedRows() { return this.rows.filter(row => row.querySelector('[data-gauge-choice]').checked); }
    rowData(row) {
      return {
        row, role:row.dataset.role, sku:upper(row.dataset.sku), label:row.dataset.label, units:this.componentQuantity(row),
        productId:row.dataset.productId, variantId:row.dataset.variantId,
        url:row.dataset.productUrl, price:Number(row.dataset.price), available:row.dataset.available === 'true'
      };
    }
    frameData() {
      return {
        role:'frame', sku:upper(this.dataset.frameSku), label:this.dataset.frameModel + ' gauge', units:1, productId:this.dataset.frameId,
        variantId:this.dataset.frameVariant, url:this.dataset.frameUrl,
        price:Number(this.dataset.framePrice), available:this.dataset.frameAvailable === 'true'
      };
    }
    refresh() {
      const quantity = this.quantity();
      const frame = this.frameData();
      const chosen = this.selectedRows().map(row => this.rowData(row));
      const rollers = chosen.filter(row => row.role === 'roller');
      const roller = rollers.length === 1 ? rollers[0] : null;
      const accessories = chosen.filter(row => row.role !== 'roller');
      const extraPrice = accessories.reduce((total,row) => total + row.price * row.units, 0);
      const validRoller = roller ? this.validRollers.has(roller.sku) && roller.available && moneyValue(roller.price) : this.rollersIncluded;
      const rollerPrice = roller ? roller.price : 0;
      const usable = rollers.length <= 1 && this.validFrame(frame) && frame.available && moneyValue(frame.price) && validRoller && quantity && chosen.every(row => row.available && moneyValue(row.price) && row.units && row.units * quantity <= 9999);
      this.querySelector('[data-gauge-frame-price]').textContent = moneyValue(frame.price) ? this.money(frame.price) : 'Not available';
      this.querySelector('[data-gauge-roller-label]').textContent = roller ? (this.rollersIncluded ? 'Extra roller set ' : 'Roller set ') + roller.sku : 'Roller set';
      this.querySelector('[data-gauge-roller-price]').textContent = roller ? this.money(roller.price) : this.rollersIncluded ? 'Included in gauge price' : 'Choose a set';
      this.querySelector('[data-gauge-accessories-price]').textContent = this.money(extraPrice);
      this.querySelector('[data-gauge-unit-total]').textContent = usable ? this.money(frame.price + rollerPrice + extraPrice) : '—';
      this.querySelector('[data-gauge-total]').textContent = usable ? this.money((frame.price + rollerPrice + extraPrice) * quantity) : '—';
      this.querySelector('[data-gauge-quantity-label]').textContent = quantity ? quantity + (quantity === 1 ? ' gauge' : ' gauges') : 'Enter a whole quantity';
      this.rows.forEach(row => {
        const input = row.querySelector('[data-gauge-choice]');
        row.classList.toggle('is-selected', input.checked);
        input.disabled = this.busy || this.uncertain || row.dataset.available !== 'true';
        const units = row.querySelector('[data-gauge-component-quantity]');
        if (units) units.disabled = input.disabled || !input.checked;
      });
      this.button.disabled = !usable || this.busy || this.uncertain;
      const clear = this.querySelector('[data-gauge-clear-roller]'); if (clear) clear.disabled = this.busy || this.uncertain;
      if (!this.busy && !this.uncertain && !this.status.classList.contains('is-error')) {
        if (!frame.available || !moneyValue(frame.price)) this.setStatus('This gauge is not currently available to add online.');
        else if (!quantity) this.setStatus('Enter a whole number of frames from 1 to 9999.');
        else if (!roller && !this.rollersIncluded) this.setStatus('Choose a compatible roller set to complete your gauge.');
        else if (chosen.some(row => !row.units || row.units * quantity > 9999)) this.setStatus('Enter a whole indicator quantity from 1 to 99 per gauge (maximum 9999 units in total).');
        else if (usable) this.setStatus('Ready to add: ' + quantity + (quantity === 1 ? ' gauge' : ' gauges') + (this.rollersIncluded ? ', supplied rollers' : ', matching rollers') + (accessories.length ? ' and ' + accessories.length + ' accessory type' + (accessories.length === 1 ? '' : 's') : '') + '.');
      }
    }
    lock(busy) {
      this.busy = busy;
      this.setAttribute('aria-busy', String(busy));
      if (busy) {
        this.oldDisabled = new Map();
        this.form.querySelectorAll('[name="quantity"], [data-action="increase-picker-quantity"], [data-action="decrease-picker-quantity"]').forEach(control => {
          this.oldDisabled.set(control, control.disabled); control.disabled = true;
        });
      } else if (this.oldDisabled) {
        this.oldDisabled.forEach((wasDisabled,control) => { control.disabled = wasDisabled; });
      }
      this.button.textContent = busy ? 'Checking selected items…' : 'Add selected items to basket';
      this.refresh();
    }
    async checkProduct(item) {
      const url = localURL(item.url);
      url.pathname = url.pathname.replace(/\/$/, '') + '.js';
      url.search = '';
      url.hash = '';
      const data = await requestJSON(url.href, {cache:'no-store', headers:{Accept:'application/json'}});
      if (String(data.id) !== String(item.productId)) throw new Error(item.sku + ': the linked product has changed. Please reload this page.');
      const variant = Array.isArray(data.variants) && data.variants.find(v => String(v.id) === String(item.variantId) && upper(v.sku) === item.sku);
      if (!variant || !variant.available || !moneyValue(variant.price)) {
        if (item.row) {
          item.row.dataset.available = 'false';
          item.row.querySelector('[data-gauge-choice]').checked = false;
          item.row.querySelector('[data-gauge-availability]').hidden = false;
        } else this.dataset.frameAvailable = 'false';
        throw new Error(item.sku + ' is not available to add online. Please review your selection.');
      }
      return {item, price:Number(variant.price)};
    }
    async addSelection() {
      if (!this.ready || this.busy || this.uncertain) return;
      const frame = this.frameData();
      const selected = this.selectedRows().map(row => this.rowData(row));
      const rollers = selected.filter(item => item.role === 'roller');
      const quantity = this.quantity();
      const variantField = this.form.querySelector('[name="id"]');
      if (rollers.length > 1 || (!rollers.length && !this.rollersIncluded) || (rollers.length && !this.validRollers.has(rollers[0].sku))) {
        this.setStatus('Please choose one roller set before adding the gauge.', true);
        this.querySelector('[data-gauge-choice]:not(:disabled)')?.focus(); return;
      }
      if (!quantity) { this.setStatus('Enter a whole number of frames from 1 to 9999.', true); this.quantityInput?.focus(); return; }
      if (!this.validFrame(frame) || !frame.available || !moneyValue(frame.price) || !variantField || String(variantField.value) !== String(frame.variantId)) {
        this.setStatus('The gauge frame could not be confirmed. Reload the page before ordering.', true); return;
      }
      if (selected.some(item => !item.units || item.units * quantity > 9999 || !item.available || !moneyValue(item.price) || !item.variantId || !item.productId || !item.url)) {
        this.setStatus('One of the selected components is unavailable. Please review your selection.', true); return;
      }
      const all = [frame, ...selected];
      if (new Set(all.map(item => item.variantId)).size !== all.length) {
        this.setStatus('The same product is linked twice. Please contact MQS before ordering.', true); return;
      }
      this.submissionComplete = false;
      this.setStatus('Checking current prices and availability…');
      this.lock(true);
      let posted = false;
      let completed = false;
      try {
        const checked = await Promise.allSettled(all.map(item => this.checkProduct(item)));
        const failed = checked.find(result => result.status === 'rejected');
        if (failed) throw failed.reason;
        let priceChanged = false;
        checked.forEach(result => {
          const {item,price} = result.value;
          if (price !== item.price) {
            priceChanged = true;
            if (item.row) {
              item.row.dataset.price = String(price);
              item.row.querySelector('[data-gauge-option-price]').textContent = '+' + this.money(price);
            } else this.dataset.framePrice = String(price);
          }
        });
        if (priceChanged) {
          this.setStatus('Prices have changed. Review the updated total, then click Add selected items to basket again.', true);
          return;
        }
        const group = groupId();
        const mainProperties = {};
        for (const [name,value] of new FormData(this.form).entries()) {
          const match = /^properties\[(.+)\]$/.exec(name);
          if (match && typeof value === 'string' && value !== '') mainProperties[match[1]] = value;
        }
        const items = all.map(item => ({
          id:Number(item.variantId), quantity:quantity * item.units,
          properties:{
            ...(item.role === 'frame' ? mainProperties : {'For gauge':this.dataset.frameModel}),
            'SKU':item.sku,
            ...(item.role === 'frame' ? {'Gauge model':this.dataset.frameModel, 'Selected roller set':rollers[0]?.sku || 'Full rollers included', 'Gauge supply':this.rollersIncluded ? 'Full rollers included; any extra roller set is additional' : 'Main rollers are a separate line; indicators optional'} : {}),
            '_mqs_gauge_group':group,
            '_mqs_gauge_role':item.role,
            ...(item.role === 'frame' ? {'_mqs_required_roller':this.rollersIncluded ? '' : rollers[0].sku, '_mqs_required_roller_id':this.rollersIncluded ? '' : String(rollers[0].variantId)} : {})
          }
        }));
        this.button.textContent = 'Adding selected items…';
        this.setStatus('Adding the frame, rollers and selected accessories…');
        posted = true;
        const result = await requestJSON(localURL(this.dataset.cartAddUrl).href, {
          method:'POST', headers:{'Content-Type':'application/json', Accept:'application/json', 'X-Requested-With':'XMLHttpRequest'}, body:JSON.stringify({items})
        });
        if (!Array.isArray(result.items) || !items.every(item => result.items
          .filter(added => String(added.variant_id || added.id) === String(item.id) && added.properties && added.properties._mqs_gauge_group === group)
          .reduce((sum, added) => sum + Number(added.quantity || 0), 0) >= item.quantity)) {
          throw new Error('The basket response did not confirm every selected item.');
        }
        completed = true;
        this.submissionComplete = true;
        this.setStatus('Selected items added. Opening your basket…');
        const cartURL = localURL(this.dataset.cartUrl);
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) cartURL.searchParams.set('preview_theme_id', preview);
        location.assign(cartURL.href);
      } catch (error) {
        if (posted) {
          this.uncertain = true;
          this.querySelector('[data-gauge-review-cart]').hidden = false;
          this.setStatus((error.message || 'The basket could not be confirmed.') + ' Review your basket before trying again, to avoid duplicate items.', true);
        } else this.setStatus(error.message || 'Current prices could not be checked. Please try again.', true);
      } finally {
        if (!completed) this.lock(false);
      }
    }
  }
  customElements.define('mqs-gauge-builder', MQSGaugeBuilder);
  // Capture Enter/requestSubmit as well as button clicks, before Warehouse's handlers.
  window.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const builder = form.querySelector('mqs-gauge-builder');
    if (builder) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (builder.ready) builder.addSelection();
      return;
    }
    if (form.querySelector('[data-mqs-gauge-cart-incomplete]')) {
      event.preventDefault(); event.stopImmediatePropagation();
      form.querySelector('[data-mqs-gauge-cart-incomplete]').scrollIntoView({block:'center'});
    }
  }, true);
})();
